import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';
import {config} from "../../config";
import Aos from 'aos'
import 'aos/dist/aos.css'

/* Aligned to the same column as the header (shared gutters) */
const Section = styled.section`
    width: 100%;
    max-width: var(--container);
    margin: 0 auto;
    padding: var(--section-y) var(--gutter);
`;

const HomePageContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    gap: 4rem;

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
        gap: 3rem;
    }
`;

const TextContent = styled.div`
    text-align: center;

    @media (min-width: 768px) {
        text-align: left;
    }
`;

const Eyebrow = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: var(--fs-eyebrow);
    font-weight: 500;
    letter-spacing: 0.02em;
    color: var(--brand-700);
    background: transparent;
    border: 1px solid var(--line-strong);
    padding: 0.45rem 1.1rem;
    border-radius: var(--radius-pill);
    margin-bottom: 1.25rem;
`;

const Title = styled.h2`
    font-weight: 500;
    font-size: var(--fs-h2);
    color: var(--ink);
    margin-bottom: 1.1rem;
    letter-spacing: -0.015em;
`;

const Description = styled.p`
    font-size: var(--fs-body);
    text-align: left;
    line-height: 1.8;
    color: var(--body);
    margin-bottom: 2rem;

    @media (max-width: 767px) {
        text-align: center;
    }
`;

const LogoContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    align-items: stretch;
    gap: 1.25rem;
`;

const LogoCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 0.9rem;
    background: transparent;
    padding: 0;
    transition: transform var(--transition);

    &:hover {
        transform: translateY(-4px);
    }

    /* On mobile, keep the white card look */
    @media (max-width: 860px) {
        background: var(--surface);
        border-radius: var(--radius-lg);
        padding: 1.75rem 1.25rem;
    }
`;

const Logo = styled.img`
    max-width: 120px;
    max-height: 80px;
    width: auto;
    object-fit: contain;
`;

const PartnerName = styled.span`
    font-size: var(--fs-sm);
    font-weight: 600;
    color: var(--ink-soft);
`;

const ContactUsButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: var(--MadekoGradient);
    color: white;
    border: none;
    border-radius: var(--radius-pill);
    padding: 0.85rem 1.8rem;
    font-size: var(--fs-sm);
    font-weight: 500;
    cursor: pointer;
    transition: transform var(--transition-fast), filter var(--transition), box-shadow var(--transition);

    &:hover {
        transform: translateY(-2px);
        filter: saturate(1.08);
        box-shadow: var(--shadow-brand);
    }
`;

const Partner = () => {
    const { t } = useTranslation();
    const [partnerData, setPartners] = useState(
        JSON.parse(localStorage.getItem('partnerData')) || []
    );

    useEffect(() => {
        const fetchPartners = async () => {
            try {
                const response = await fetch(`${config.baseUrl}/partners`);
                const data = await response.json();

                // Only update if new data is different from localStorage
                const storedData = JSON.parse(localStorage.getItem('partnerData')) || [];
                if (JSON.stringify(data) !== JSON.stringify(storedData)) {
                    setPartners(data);
                    localStorage.setItem('partnerData', JSON.stringify(data));
                }
            } catch (error) {
                console.error('Error fetching partners:', error);
            }
        };

        fetchPartners();
    }, [partnerData.length]);

    // Smooth scroll function
    const scrollToSection = (elementId) => {
        const element = document.getElementById(elementId);
        if (element) {
            // Smooth scroll
            element.scrollIntoView({ behavior: 'smooth' });
        }
    }

    useEffect(() => {
        Aos.init({
            duration: 2000,
            once: true
        });
    }, []);

    return (
        <Section>
            <HomePageContainer>
                <TextContent
                    data-aos-duration="2000"
                    data-aos-once="true"
                    data-aos="fade-right"
                >
                    <Eyebrow>{t('partners.eyebrow')}</Eyebrow>
                    <Title> {t('partners.title')} </Title>
                    <Description>
                        <Trans
                            i18nKey="partners.description"
                            components={{ hl: <span style={{ color: "var(--highlight)", fontWeight: 600 }}/> }}
                        />
                    </Description>
                    <ContactUsButton onClick={() => scrollToSection('contact')}>{t('partners.contactUs')}</ContactUsButton>
                </TextContent>
                <LogoContainer
                    data-aos-duration="2000"
                    data-aos-once="true"
                    data-aos="fade-left"
                >
                    {partnerData.map((partner) => (
                        <LogoCard key={partner.id}>
                            <Logo src={partner.logo} alt={partner.name} />
                            <PartnerName>{partner.name}</PartnerName>
                        </LogoCard>
                    ))}
                </LogoContainer>
            </HomePageContainer>
        </Section>
    );
};

export default Partner;
